import { PortableText } from '@portabletext/react'
import { clsx } from 'clsx'
import { motion } from 'framer-motion'
import { useState, useCallback } from 'react'

import { ArrowForwardIcon } from 'Icons'

import type { WheelItemsData, WheelItem } from '../wheelData'

import DetailedDescription from './DetailedDescription'
import FeelingBadge from './FeelingBadge'

type ShortDescriptionProps = {
  activeItems: Set<WheelItem>
  wheelItemsData: WheelItemsData
}

export default function ShortDescription({ activeItems, wheelItemsData }: ShortDescriptionProps) {
  const [selectedItem, setSelectedItem] = useState<WheelItem | null>(null)

  const handleBack = useCallback(() => {
    setSelectedItem(null)
  }, [])

  if (selectedItem) {
    return (
      <DetailedDescription
        item={selectedItem}
        wheelItemsData={wheelItemsData}
        onBack={handleBack}
      />
    )
  } else {
    return (
      <div className="flex flex-1 flex-col gap-4">
        {Array.from(activeItems).map((item) => (
          <ShortDescriptionCard
            key={item}
            item={item}
            wheelItemsData={wheelItemsData}
            setSelectedItem={setSelectedItem}
          />
        ))}
      </div>
    )
  }
}

type ShortDescriptionCardProps = {
  item: WheelItem
  wheelItemsData: WheelItemsData
  setSelectedItem: (item: WheelItem) => void
}

function ShortDescriptionCard({
  item,
  wheelItemsData,
  setSelectedItem,
}: ShortDescriptionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={clsx(
        'card card-sm bg-base-300 hover:bg-base-200 cursor-pointer shadow-sm transition-colors hover:shadow-lg dark:hover:bg-slate-950',
      )}
      onClick={() => setSelectedItem(item)}
    >
      <div className="card-body">
        <div className="card-title flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-2">
            <FeelingBadge className="h-4 w-4" item={item} wheelItemsData={wheelItemsData} />
            <h2 className="text-xl uppercase">{wheelItemsData[item].label.label}</h2>
          </div>
          <Shevron />
        </div>
        <article className="prose max-w-none">
          <PortableText key={item} value={wheelItemsData[item].descriptionShort} />
        </article>
      </div>
    </motion.div>
  )
}

function Shevron() {
  return (
    <div className="flex items-center justify-center opacity-80">
      <ArrowForwardIcon />
    </div>
  )
}
